import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import type { PresentationPrivateVault } from '../../application';
import type { PassphraseCreationOrigin } from '../security/passphrase-creation.component';
import { FuneralWish } from './funeral-wish';
import { Guardianship } from './guardianship';
import { JourneyDashboard } from './journey-dashboard';
import { JourneyStore } from './journey.store';
import type {
  DashboardPart,
  JourneyView,
  PrivateCollectionId,
  SpiritualSectionId,
} from './journey.types';
import { PRIVATE_COLLECTIONS, PrivateCollections } from './private-collections';
import { PublicJourneyConfig } from './public-journey.dependencies';
import { SPIRITUAL_SECTION_IDS, SpiritualBalance } from './spiritual-balance';

export const WISHES_TAB = 'volontes';
export const WASI_TAB = 'wasi';
export const DOCUMENTS_TAB = 'documents';
export const SETTINGS_TAB = 'reglages';

/**
 * Where each door of the journey leads: the tabs, the lines of the dashboard, and the private
 * door in front of everything behind the phrase. Screens ask to go somewhere; this knows what has
 * to be ready first, and where to land. Provided with the journey.
 */
@Injectable()
export class JourneyNavigation {
  private readonly store = inject(JourneyStore);
  private readonly config = inject(PublicJourneyConfig);
  private readonly i18n = inject(TranslocoService);
  private readonly dashboard = inject(JourneyDashboard);
  private readonly collections = inject(PrivateCollections);
  private readonly spiritual = inject(SpiritualBalance);
  private readonly guardianship = inject(Guardianship);
  private readonly funeral = inject(FuneralWish);

  /**
   * Switching tab is movement inside the session, not a way out of it: the vault stays open,
   * exactly as returning to the dashboard already did.
   */
  selectTab(id: string): void {
    this.store.session.touch();
    this.store.situationUpdated.set(false);
    if (id === DOCUMENTS_TAB) {
      this.store.go('documents');
      // The print button is one tap away now. Whatever printing needs, it should already be
      // here when that tap comes: iOS only opens the dialog while the gesture is still live.
      this.config.preparePrinting();
      return;
    }
    if (id === SETTINGS_TAB) {
      this.store.go('settings');
      return;
    }
    if (id === WASI_TAB) {
      this.openExecutorDesignation();
      return;
    }
    // Pressing the tab itself goes to the top of that tab, not back into the part last opened.
    this.store.activeDashboardPart.set(null);
    this.store.enterDashboard();
  }

  openDashboardPart(part: DashboardPart): void {
    this.store.session.touch();
    this.store.activeDashboardPart.set(part);
    this.store.go('dashboard-part');
  }

  /**
   * The same door as the row on the part screen, not a shortcut past it: the part is entered
   * first, so leaving the section lands there rather than back on the grid. A private line still
   * goes through the vault, because that is what `openDashboardSection` does.
   */
  followNextStep(): void {
    this.store.session.touch();
    const step = this.dashboard.nextStep();
    if (step.target === null) {
      this.selectTab(DOCUMENTS_TAB);
      return;
    }
    // The wasi is a tab, not a line of a part: no part is entered on the way.
    if (step.target.part === null) {
      this.selectTab(WASI_TAB);
      return;
    }
    this.store.activeDashboardPart.set(step.target.part);
    this.openDashboardSection(step.target.sectionId);
  }

  /** The Backup screen stays in Settings; from the Wasi tab, back leads to the tab again. */
  openBackupFromWasi(): void {
    this.store.session.touch();
    this.store.backupReturnView.set('executor');
    this.store.go('backup');
  }

  openDashboardSection(sectionId: string): void {
    if (sectionId === 'guardianship') {
      void this.openGuardianship();
      return;
    }
    if (sectionId === 'estate') {
      void this.openEstate();
      return;
    }
    if (sectionId === 'executor') {
      this.openExecutorDesignation();
      return;
    }
    if (sectionId === 'origin-burial') {
      void this.funeral.open();
      return;
    }
    if (SPIRITUAL_SECTION_IDS.includes(sectionId as SpiritualSectionId)) {
      void this.openSpiritualBalance(sectionId as SpiritualSectionId);
      return;
    }
    const collectionId = sectionId as PrivateCollectionId;
    if (PRIVATE_COLLECTIONS[collectionId] !== undefined) {
      void this.openPrivateCollection(collectionId);
    }
  }

  /**
   * The row opens the screen where the wasi stands; the pencil on the emergency card asks for the
   * form outright, because correcting what is printed there is the only thing it can mean.
   */
  openExecutorDesignation(correcting = false): void {
    this.store.personReturnView.set(this.store.view() === 'emergency' ? 'emergency' : 'dashboard');
    this.store.executorCorrecting.set(correcting);
    this.store.errorMessage.set('');
    this.store.go('executor');
  }

  openPassphraseCreation(origin: PassphraseCreationOrigin): void {
    this.store.session.touch();
    this.store.errorMessage.set('');
    this.store.creationOrigin.set(origin);
    this.store.go('passphrase-creation');
  }

  /** The terms or the policy, and back to the screen whose link opened them. */
  openLegal(document: 'terms' | 'privacy'): void {
    this.store.session.touch();
    this.store.legalReturn.set(this.store.view());
    this.store.go(document === 'terms' ? 'legal-terms' : 'legal-privacy');
  }

  /** Asks for the phrase on the way back to a screen — the phrase screen, the backup — not into a section. */
  openUnlockFor(view: JourneyView): void {
    this.store.unlockReturn.set(view);
    this.store.errorMessage.set('');
    this.store.go('unlock');
  }

  /** The last profile question is saved: on to the dashboard, or back to Settings. */
  async profileCompleted(): Promise<void> {
    await this.dashboard.refresh();
    this.store.resumeAvailable.set(true);
    if (this.store.revisitingProfile()) {
      this.store.revisitingProfile.set(false);
      this.store.situationUpdated.set(true);
      this.store.go('settings');
      return;
    }
    this.store.enterDashboard();
  }

  async openGuardianship(): Promise<void> {
    this.store.activePrivateSectionId.set('guardianship');
    await this.throughPrivateDoor(async () => {
      await this.guardianship.ensureSection();
      return () => {
        this.guardianship.load(this.store.session.requireVault());
        this.store.go('guardianship');
      };
    });
  }

  /** The estimate of the estate: its screen reads the vault itself, nothing to prepare. */
  async openEstate(): Promise<void> {
    this.store.activePrivateSectionId.set('estate');
    await this.throughPrivateDoor(() => Promise.resolve(() => this.store.go('estate')));
  }

  async openPrivateCollection(collectionId: PrivateCollectionId): Promise<void> {
    this.store.activeCollectionId.set(collectionId);
    this.store.activePrivateSectionId.set(collectionId);
    this.collections.reset();
    await this.throughPrivateDoor(async () => {
      await this.collections.ensureSection();
      return () => {
        this.collections.load(this.store.session.requireVault());
        this.store.go('private-collection');
      };
    });
  }

  async openSpiritualBalance(section: SpiritualSectionId = 'fasting'): Promise<void> {
    this.store.activePrivateSectionId.set(section);
    await this.throughPrivateDoor(async () => {
      await this.preparePrivateTarget();
      return () => this.store.go('spiritual-balance');
    });
  }

  /**
   * A private part asked for: what it needs is resolved first; then, if the vault is open, the
   * part itself — otherwise its phrase if there is one, the way to create one if not. Either way
   * the reader comes back to the part they asked for.
   */
  private async throughPrivateDoor(prepare: () => Promise<() => void>): Promise<void> {
    this.store.busy.set(true);
    this.store.errorMessage.set('');
    try {
      const enter = await prepare();
      if (this.store.session.isUnlocked) {
        enter();
        this.store.session.touch();
      } else {
        this.openPrivateDoor();
      }
    } catch (error: unknown) {
      this.store.fail(error, this.i18n.translate('errors.unexpected'));
    } finally {
      this.store.busy.set(false);
    }
  }

  openPrivateDoor(options: { readonly replace?: boolean } = {}): void {
    if (this.store.draft()?.encryptedPrivateVault === null) {
      this.openPassphraseCreation('private-section');
      return;
    }
    this.store.go('unlock', options);
  }

  /** Whatever the private part asked for needs, resolved before the phrase is tried. */
  async preparePrivateTarget(): Promise<void> {
    if (this.store.unlockReturn() !== null) return;
    if (this.store.activePrivateSectionId() === 'estate') return;
    if (this.isGuardianshipTarget()) {
      await this.guardianship.ensureSection();
      return;
    }
    if (this.isSpiritualTarget()) {
      await this.spiritual.ensureSections();
      return;
    }
    await this.collections.ensureSection();
  }

  /**
   * Once the vault is readable — freshly created or just unlocked — land on the screen the
   * owner actually asked for, not on the collection screen by default.
   */
  enterPrivateTarget(vault: PresentationPrivateVault): void {
    const returnTo = this.store.unlockReturn();
    if (returnTo !== null) {
      this.store.unlockReturn.set(null);
      this.store.go(returnTo, { replace: true });
      return;
    }
    if (this.isGuardianshipTarget()) {
      this.guardianship.load(vault);
      this.store.go('guardianship', { replace: true });
      return;
    }
    if (this.store.activePrivateSectionId() === 'estate') {
      this.store.go('estate', { replace: true });
      return;
    }
    if (this.isSpiritualTarget()) {
      this.spiritual.load(vault);
      this.store.go('spiritual-balance', { replace: true });
      return;
    }
    this.collections.load(vault);
    this.store.go('private-collection', { replace: true });
  }

  private isGuardianshipTarget(): boolean {
    return this.store.activePrivateSectionId() === 'guardianship';
  }

  private isSpiritualTarget(): boolean {
    return SPIRITUAL_SECTION_IDS.includes(
      this.store.activePrivateSectionId() as SpiritualSectionId,
    );
  }
}
